import { Injectable } from '@angular/core';
import { HistoryEntry } from '../model/HistoryEntry';

const STORAGE_KEY = 'unipd_admission_history';
const MAX_ENTRIES = 10;
const SCHEMA_VERSION = 1;

@Injectable({ providedIn: 'root' })
export class HistoryService {

  list(): HistoryEntry[] {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed
        .filter((e: any) => e && e.version === SCHEMA_VERSION && e.id)
        .sort((a: HistoryEntry, b: HistoryEntry) => b.savedAt.localeCompare(a.savedAt));
    } catch {
      return [];
    }
  }

  getById(id: string): HistoryEntry | null {
    return this.list().find(e => e.id === id) || null;
  }

  save(entry: Omit<HistoryEntry, 'id' | 'savedAt' | 'version'>): HistoryEntry {
    const current = this.list();
    const fingerprint = this.fingerprint(entry);
    const deduped = current.filter(e => this.fingerprint(e) !== fingerprint);

    const full: HistoryEntry = {
      ...entry,
      version: SCHEMA_VERSION,
      id: this.makeId(),
      savedAt: new Date().toISOString(),
    };

    const next = [full, ...deduped].slice(0, MAX_ENTRIES);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    return full;
  }

  delete(id: string): void {
    const next = this.list().filter(e => e.id !== id);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  }

  clear(): void {
    localStorage.removeItem(STORAGE_KEY);
  }

  private makeId(): string {
    return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  }

  private fingerprint(e: Pick<HistoryEntry, 'course' | 'admission'>): string {
    const a = e.admission || ({} as any);
    const examsKey = (a.exams || [])
      .map((x: any) => `${x.ssd || ''}|${x.cfu || ''}|${x.vote ?? ''}`)
      .sort()
      .join(';');
    return [
      e.course,
      a.degree_type,
      a.exam_vote,
      a.languageLevel,
      a.examsAverage,
      examsKey,
    ].join('::');
  }
}
